import { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppContext from '../../../../context/AppContext';
import "../../../../assets/css/customer/publicCustomerCss/FAQ/faqPage.css";

const ROLE_META = {
  customer: { label: 'Customers', icon: '🧳' },
  vendor:   { label: 'Vendors',   icon: '🏪' },
  general:  { label: 'General',   icon: '🌐' },
};

const FAQPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUrl, authToken } = useContext(AppContext);

  const [userRole,  setUserRole]  = useState(null);
  const [faqs,      setFaqs]      = useState([]);
  const [activeTab, setActiveTab] = useState('customer');
  const [faqCache,  setFaqCache]  = useState({});
  const [openIndex, setOpenIndex] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (location.state?.scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    setLoading(true);

    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

    axios.get(`${baseUrl}/User-faqs`, { headers })
      .then((res) => {
        if (res.data.success) {
          const role = res.data.role;
          setUserRole(role);
          setActiveTab(role);
          setFaqCache({ [role]: res.data.data });
          setFaqs(res.data.data);
        }
      })
      .catch((err) => {
        setError('Unable to load FAQs');
        console.log(err);
      })
      .finally(() => setLoading(false));

  }, [baseUrl, authToken]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setOpenIndex(null);

    if (faqCache[tab]) {
      setFaqs(faqCache[tab]);
      return;
    }

    setLoading(true);

    axios.get(`${baseUrl}/User-faqs`, {
      params: { role: tab }
    })
      .then((res) => {
        if (res.data.success) {
          setFaqCache(prev => ({ ...prev, [tab]: res.data.data }));
          setFaqs(res.data.data);
        }
      })
      .catch(() => setError('Unable to load FAQs'))
      .finally(() => setLoading(false));
  };

  const handleBackClick = (e) => {
    e.preventDefault();
    navigate('/', { state: { scrollToFooter: true } });
  };

  const currentMeta = ROLE_META[activeTab] || ROLE_META.general;

  if (userRole === 'vendor') {
    return (
      <div className="faq-page">
        <div className="faq-hero">
          <div className="faq-hero-inner">
            <a className="faq-back-btn" onClick={handleBackClick} href="#">← Back to Explorer</a>
            <h1>Frequently Asked <span>Questions</span></h1>
            <p className="faq-hero-sub">Vendor Portal</p>
          </div>
        </div>
        <div className="faq-content">
          <div className="faq-section-head">
            <div className="faq-section-icon">🏪</div>
            <div>
              <div className="faq-section-title">Vendors — FAQs</div>
              <div className="faq-section-sub">
                {loading ? 'Loading…' : `${faqs.length} questions & answers`}
              </div>
            </div>
          </div>
          {loading && <div className="faq-loading">Loading FAQs…</div>}
          {error   && <div className="faq-error">{error}</div>}
          {!loading && !error && faqs.length === 0 && (
            <div className="faq-empty">No FAQs available at this time.</div>
          )}
          {!loading && faqs.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={item.id || i} className={`faq-item ${isOpen ? 'open' : ''}`}
                onClick={() => setOpenIndex(isOpen ? null : i)}>
                <div className="faq-question">
                  <div className="faq-q-text">{item.faq_question}</div>
                  <div className="faq-toggle">{isOpen ? '−' : '+'}</div>
                </div>
                <div className="faq-answer">
                  <div className="faq-answer-inner">{item.faq_answer}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (userRole === 'customer') {
    return (
      <div className="faq-page">
        <div className="faq-hero">
          <div className="faq-hero-inner">
            <a className="faq-back-btn" onClick={handleBackClick} href="#">← Back to Explorer</a>
            <h1>Frequently Asked <span>Questions</span></h1>
            <p className="faq-hero-sub">Everything you need to know</p>
          </div>
        </div>
        <div className="faq-content">
          <div className="faq-section-head">
            <div className="faq-section-icon">🧳</div>
            <div>
              <div className="faq-section-title">Customers — FAQs</div>
              <div className="faq-section-sub">
                {loading ? 'Loading…' : `${faqs.length} questions & answers`}
              </div>
            </div>
          </div>
          {loading && <div className="faq-loading">Loading FAQs…</div>}
          {error   && <div className="faq-error">{error}</div>}
          {!loading && !error && faqs.length === 0 && (
            <div className="faq-empty">No FAQs available at this time.</div>
          )}
          {!loading && faqs.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={item.id || i} className={`faq-item ${isOpen ? 'open' : ''}`}
                onClick={() => setOpenIndex(isOpen ? null : i)}>
                <div className="faq-question">
                  <div className="faq-q-text">{item.faq_question}</div>
                  <div className="faq-toggle">{isOpen ? '−' : '+'}</div>
                </div>
                <div className="faq-answer">
                  <div className="faq-answer-inner">{item.faq_answer}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="faq-page">
      <div className="faq-hero">
        <div className="faq-hero-inner">
          <a className="faq-back-btn" onClick={handleBackClick} href="#">← Back to Explorer</a>
          <h1>Frequently Asked <span>Questions</span></h1>
          <p className="faq-hero-sub">Find answers to common questions</p>
        </div>
      </div>
      <div className="faq-content">
        <div className="faq-section-head">
          <div className="faq-section-icon">🌐</div>
          <div>
            <div className="faq-section-title">General — FAQs</div>
            <div className="faq-section-sub">
              {loading ? 'Loading…' : `${faqs.length} questions & answers`}
            </div>
          </div>
        </div>
        {loading && <div className="faq-loading">Loading FAQs…</div>}
        {error   && <div className="faq-error">{error}</div>}
        {!loading && !error && faqs.length === 0 && (
          <div className="faq-empty">No FAQs available at this time.</div>
        )}
        {!loading && faqs.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={item.id || i} className={`faq-item ${isOpen ? 'open' : ''}`}
              onClick={() => setOpenIndex(isOpen ? null : i)}>
              <div className="faq-question">
                <div className="faq-q-text">{item.faq_question}</div>
                <div className="faq-toggle">{isOpen ? '−' : '+'}</div>
              </div>
              <div className="faq-answer">
                <div className="faq-answer-inner">{item.faq_answer}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FAQPage;